import React, { FunctionComponent, useState } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "./VirtualSchoolSignup.scss";
import { useNavigate } from "react-router-dom";
import images from "../../../assets/images";
import ToasterWithMessage from "../../../components/toaster";
import { useMutation, useQuery } from "@apollo/client";
import {
  VERIFY_EXISTING_STUDENT,
  GET_REGISTRATION_OPTIONS,
  GET_PROGRAMME_CATALOGUE,
  REGISTER_STUDENT_FOR_PROGRAMME,
} from "../api/AdminLoginQuery";
import ButtonStyle from "../../../components/ButtonStyle/ButtonStyle";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Select, { components } from "react-select";
import { ExpandLess, ExpandMore, InfoOutlined } from "@mui/icons-material";
import { messages, patterns } from "../../../utils/constants";

export interface IProgrammeItem {
  programmeId: string;
  code?: string;
  name: string;
  description?: string;
  price?: string | number;
  currency?: string;
}

const getCustomSelectStyles = (hasError?: boolean) => ({
  container: (provided: any) => ({
    ...provided,
    width: "100%",
  }),
  control: (provided: any, state: { selectProps: { menuIsOpen: boolean } }) => ({
    ...provided,
    minHeight: "41px",
    height: "41px",
    borderRadius: state?.selectProps?.menuIsOpen ? "10px 10px 0px 0px" : "10px",
    border: hasError ? "1px solid #ef4444" : "1px solid #ffffff",
    backgroundColor: "#ffffff",
    boxShadow: "none",
    cursor: "pointer",
    "&:hover": {
      border: hasError ? "1px solid #ef4444" : "1px solid #ffffff",
    },
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    padding: "0 12px",
    height: "41px",
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontFamily: "inherit",
    fontSize: "12px",
    fontWeight: 500,
    color: "#B1B1B1",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontFamily: "inherit",
    fontSize: "12px",
    fontWeight: 500,
    color: "#111827",
  }),
  menu: (provided: any) => ({
    ...provided,
    marginTop: "0",
    borderRadius: "0 0 10px 10px",
    backgroundColor: "#ffffff",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    zIndex: 99,
    maxHeight: "180px",
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: "4px 0",
    maxHeight: "180px",
    overflowY: "auto",
  }),
  option: (provided: any, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...provided,
    fontFamily: "inherit",
    fontSize: "12px",
    fontWeight: 400,
    color: state.isSelected ? "#ffffff" : "#111827",
    backgroundColor: state.isSelected
      ? "#0088cc"
      : state.isFocused
      ? "#f3f4f6"
      : "#ffffff",
    padding: "8px 12px",
    cursor: "pointer",
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    padding: "0 12px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
});

const CustomDropdownIndicator = (props: any) => {
  const { selectProps } = props;
  const isMenuOpen = selectProps.menuIsOpen;
  return (
    <components.DropdownIndicator {...props}>
      {isMenuOpen ? (
        <ExpandLess style={{ color: "#B1B1B1" }} fontSize="small" />
      ) : (
        <ExpandMore style={{ color: "#B1B1B1" }} fontSize="small" />
      )}
    </components.DropdownIndicator>
  );
};

interface VirtualSchoolSignupProps {
  title?: string;
  loginpage?: string;
  setSignupSuccess?: (val: boolean) => void;
}

export interface IVirtualSchoolFormInput {
  hasAccount: "yes" | "no";

  username?: string;
  password?: string;

  email?: string;
  preferredEmail: string;
  preferredPhone: string;
  phone?: string;

  fullName: string;
  dob: string;
  gender: string;

  parentName: string;
  relationship: string;
  parentPhone: string;
  parentEmail?: string;

  currentPreviousSchoolName: string;
  academicLevel: string;
  beceNumber: string;
  program: string;

  registeredSimName: string;
  networkProvider: string;
  location: string;
  referralSource: string;
  communicationGroups: string[];
  dataOptInFor?: string;
}

const schema = yup.object().shape({
  hasAccount: yup.string().required(),
  username: yup.string().when("hasAccount", {
    is: "yes",
    then: (s) => s.required("Username is required to verify account"),
    otherwise: (s) =>
      s
        .strict()
        .trim(messages.invalidUsername)
        .required(messages.username)
        .matches(patterns.username, messages.validUsername)
        .matches(patterns.usernameStartWithAlphabets, messages.usernameStartWithAlphabets)
        .matches(patterns.consecutiveDotsDashesUnderscores, messages.noConsecutiveDotsDashesUnderscores)
        .min(6, messages.usernameMinLength)
        .max(50, messages.usernameMaxLength)
        .test("not-only-numbers", messages.invalidUsername, (value) => {
          if (value) {
            return !patterns.onlyNumbers.test(value);
          }
          return true;
        }),
  }),
  password: yup.string().when("hasAccount", {
    is: "yes",
    then: (s) => s.required("Password is required to verify account"),
    otherwise: (s) =>
      s
        .strict()
        .trim(messages.invalidPassword)
        .required(messages.password)
        .matches(patterns?.password, messages?.validPassword)
        .min(8, messages.passwordMinLength)
        .max(30, messages.passwordMaxLength),
  }),
  email: yup.string().when("hasAccount", {
    is: "no",
    then: (s) =>
      s
        .required("Email is required")
        .trim()
        .test("is-valid-email", messages.validEmail, function (value) {
          const { path, createError } = this;
          if (value && value.length > 0) {
            return patterns.email.test(value) || createError({ path, message: messages.validEmail });
          }
          return true;
        }),
    otherwise: (s) => s.optional().nullable(),
  }),
  preferredEmail: yup
    .string()
    .trim()
    .required("Email is required")
    .test("is-valid-preferredEmail", messages.validEmail, function (value) {
      const { path, createError } = this;
      if (value && value.length > 0) {
        return patterns.email.test(value) || createError({ path, message: messages.validEmail });
      }
      return true;
    }),
  preferredPhone: yup
    .string()
    .required("Contact number is required")
    .test("is-valid-preferredPhone", messages.validPhoneNumber, function (value) {
      const { path, createError } = this;
      if (!value) return true;
      const startsWithPlus = value.startsWith("+");
      if (!patterns.phoneNumber.test(value)) {
        return createError({ path, message: messages.validPhoneNumber });
      }
      if (startsWithPlus && value.length > 20) {
        return createError({ path, message: messages.phoneNumberLength });
      } else if (!startsWithPlus && value.length > 16) {
        return createError({ path, message: messages.phoneNumberLength16 });
      }
      return true;
    }),
  phone: yup.string().when("hasAccount", {
    is: "no",
    then: (s) =>
      s
        .optional()
        .nullable()
        .test("is-valid-phoneNumber", messages.validPhoneNumber, function (value) {
          const { path, createError } = this;
          if (!value) return true;
          const startsWithPlus = value.startsWith("+");
          if (!patterns.phoneNumber.test(value)) {
            return createError({ path, message: messages.validPhoneNumber });
          }
          if (startsWithPlus && value.length > 20) {
            return createError({ path, message: messages.phoneNumberLength });
          } else if (!startsWithPlus && value.length > 16) {
            return createError({ path, message: messages.phoneNumberLength16 });
          }
          return true;
        }),
    otherwise: (s) => s.optional().nullable(),
  }),

  fullName: yup.string().when("hasAccount", {
    is: "no",
    then: (s) =>
      s
        .required(messages?.fullname)
        .trim()
        .max(100, "Full name should not exceed 100 characters"),
    otherwise: (s) => s.optional().nullable(),
  }),
  dob: yup
    .string()
    .required("Date of birth is required")
    .test("no-future-date", "Date of birth cannot be in the future", (value) => {
      if (!value) return true;
      return dayjs(value).isBefore(dayjs().endOf("day"));
    }),
  gender: yup.string().required("Gender is required"),

  parentName: yup
    .string()
    .required("Parent name is required")
    .trim()
    .max(100, "Parent name should not exceed 100 characters"),
  relationship: yup.string().required("Relationship is required"),
  parentPhone: yup
    .string()
    .required("Parent phone number is required")
    .test("is-valid-parentPhone", messages.validPhoneNumber, function (value) {
      const { path, createError } = this;
      if (!value) return true;
      const startsWithPlus = value.startsWith("+");
      if (!patterns.phoneNumber.test(value)) {
        return createError({ path, message: messages.validPhoneNumber });
      }
      if (startsWithPlus && value.length > 20) {
        return createError({ path, message: messages.phoneNumberLength });
      } else if (!startsWithPlus && value.length > 16) {
        return createError({ path, message: messages.phoneNumberLength16 });
      }
      return true;
    }),
  parentEmail: yup
    .string()
    .optional()
    .nullable()
    .trim()
    .test("is-valid-parentEmail", messages.validEmail, function (value) {
      const { path, createError } = this;
      if (value && value.length > 0) {
        return patterns.email.test(value) || createError({ path, message: messages.validEmail });
      }
      return true;
    }),

  currentPreviousSchoolName: yup
    .string()
    .required("School name is required")
    .trim()
    .max(100, "School name should not exceed 100 characters"),
  academicLevel: yup.string().required("Academic level is required"),
  beceNumber: yup
    .string()
    .required("BECE number is required")
    .matches(patterns.onlyNumbers, "BECE number must contain only numbers")
    .max(12, "BECE number should not exceed 12 digits"),
  program: yup.string().required("Program is required"),

  registeredSimName: yup.string().optional().nullable(),
  networkProvider: yup.string().optional().nullable(),
  location: yup.string().required("Location is required"),
  referralSource: yup.string().optional().nullable(),
  communicationGroups: yup.array().of(yup.string()).optional(),
  dataOptInFor: yup.string().optional().nullable(),
});

const VirtualSchoolSignup: FunctionComponent<VirtualSchoolSignupProps> = ({
  title,
  loginpage,
  setSignupSuccess,
}) => {
  const navigate = useNavigate();
  const { backArrow, CalenderGrey, eyeon, eyeoff } = images;
  const { success, error } = ToasterWithMessage();

  // Additional component states
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

  const [verifyExistingStudent, { loading: isVerifying }] = useMutation(VERIFY_EXISTING_STUDENT);
  const [registerStudentForProgramme, { loading: isRegistering }] = useMutation(REGISTER_STUDENT_FOR_PROGRAMME);
  const { data: regOptionsData } = useQuery(GET_REGISTRATION_OPTIONS);
  const { data: programmeData } = useQuery(GET_PROGRAMME_CATALOGUE);

  const regOptions = regOptionsData?.registrationOptions;
  const genderOptions: string[] = regOptions?.genders || ["Male", "Female", "Prefer not to say"];
  const dataOptInTargetsOptions: string[] = regOptions?.dataOptInTargets || ["Student Number", "Parent/Guardian Number"];
  const relationshipOptions: string[] = regOptions?.guardianRelationships || ["Mother", "Father", "Guardian", "Sibling", "Other"];
  const academicLevelOptions: string[] = regOptions?.academicLevels || ["JHS 1", "JHS 2", "JHS 3", "SHS 1", "SHS 2", "SHS 3", "Other"];
  const networkProviderOptions: string[] = regOptions?.networkProviders || ["MTN", "Telecel", "AirtelTigo", "Glo", "Other"];
  const locationOptions: string[] = regOptions?.locations || [
    "Ahafo", "Ashanti", "Bono", "Bono East", "Central", "Eastern",
    "Greater Accra", "North East", "Northern", "Oti", "Savannah",
    "Upper East", "Upper West", "Volta", "Western", "Western North"
  ];
  const communicationGroupOptions: string[] = regOptions?.communicationGroups || [
    "Add to Parents WhatsApp group chat",
    "Add to Students WhatsApp group chat"
  ];
  const referralSourceOptions: string[] = regOptions?.referralSources || [
    "Word of Mouth", "Instagram", "Radio", "Sign Board / Advertisment", "Other"
  ];

  const programmeList: IProgrammeItem[] = programmeData?.programmeCatalogue || [];


  const methods = useForm<IVirtualSchoolFormInput>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      hasAccount: "yes",
      username: "",
      password: "",
      email: "",
      preferredEmail: "",
      preferredPhone: "",
      phone: "",
      fullName: "",
      dob: "",
      gender: "",
      parentName: "",
      relationship: "",
      parentPhone: "",
      parentEmail: "",
      currentPreviousSchoolName: "",
      academicLevel: "",
      beceNumber: "",
      program: "",
      registeredSimName: "",
      networkProvider: "",
      location: "",
      referralSource: "",
      communicationGroups: [],
      dataOptInFor: "",
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    control,
    reset,
    formState: { errors },
  } = methods;

  const handleRadioChange = (choice: "yes" | "no") => {
    reset({
      hasAccount: choice,
      username: "",
      password: "",
      email: "",
      preferredEmail: "",
      preferredPhone: "",
      phone: "",
      fullName: "",
      dob: "",
      gender: "",
      parentName: "",
      relationship: "",
      parentPhone: "",
      parentEmail: "",
      currentPreviousSchoolName: "",
      academicLevel: "",
      beceNumber: "",
      program: "",
      registeredSimName: "",
      networkProvider: "",
      location: "",
      referralSource: "",
      communicationGroups: [],
      dataOptInFor: "",
    });
    setIsVerified(false);
  };

  const hasAccountChoice = watch("hasAccount");

  const usernameVal = watch("username");
  const passwordVal = watch("password");

  React.useEffect(() => {
    if (isVerified) {
      setIsVerified(false);
    }
  }, [hasAccountChoice, usernameVal, passwordVal]);

  React.useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsRedirecting(false);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  const handleVerifyAccount = async () => {
    const uVal = getValues("username");
    const pVal = getValues("password");

    if (!uVal || !pVal) {
      error("Please enter both Username and Password to verify.");
      return;
    }

    try {
      const response = await verifyExistingStudent({
        variables: {
          input: {
            userName: uVal,
            passWord: pVal,
          },
        },
      });

      const result = response?.data?.verifyExistingStudent;

      if (result?.verified) {
        if (result?.alreadyInVirtualClassroom) {
          setIsVerified(false);
          error(result?.message || "This account is already registered in Katon Virtual School.");
        } else {
          setIsVerified(true);
          success(result?.message || "Account verified successfully!");
          if (result?.fullName) {
            setValue("fullName", result.fullName);
          }
          if (result?.email) {
            setValue("email", result.email);
          }
          if (result?.phoneNumber) {
            setValue("phone", result?.phoneNumber);
          }
        }
      } else {
        setIsVerified(false);
        error(result?.message || "Verification failed. Please check your username and password.");
      }
    } catch (err: any) {
      setIsVerified(false);
      error(err?.message || "Failed to verify account. Please try again.");
    }
  };

  const onSubmit = async (data: IVirtualSchoolFormInput) => {
    const isExisting = hasAccountChoice === "yes";

    if (isExisting && !isVerified) {
      error("Please verify your existing Katon account before continuing.");
      return;
    }

    const inputPayload: any = {
      programmeId: data.program,
      isExistingUser: isExisting,
      userName: data.username,
      passWord: data.password,
      communicationEmail: data.preferredEmail,
      communicationPhone: data.preferredPhone,
      guardianName: data.parentName,
      guardianPhone: data.parentPhone,
      guardianEmail: data.parentEmail,
      guardianRelationship: data.relationship,
      consentAccepted: true,
      details: {
        gender: data.gender,
        dateOfBirth: data.dob,
        currentLevel: data.academicLevel,
        previousSchool: data.currentPreviousSchoolName,
        indexNumber: data.beceNumber,
        simName: data.registeredSimName,
        networkProvider: data.networkProvider,
        location: data.location,
        communicationGroups: data.communicationGroups,
        referralSource: data.referralSource || undefined,
        dataOptInFor: data.dataOptInFor || undefined,
      },
    };

    if (!isExisting) {
      inputPayload.fullName = data.fullName;
      inputPayload.email = data.email;
      inputPayload.phoneNumber = data.phone;
    }

    try {
      const response = await registerStudentForProgramme({
        variables: {
          input: inputPayload,
        },
      });

      const result = response?.data?.registerStudentForProgramme;

      if (result) {
        if (result.tokens) {
          localStorage.setItem("vs_temp_tokens", JSON.stringify(result.tokens));
        }
        const savedUser = result.userName || data.username;
        if (savedUser) {
          localStorage.setItem("registered_username", savedUser);
        }

        if (result.paymentLink) {
          success("Registration complete. Redirecting you to payment gateway...");
          setIsRedirecting(true);
          reset();
          setIsVerified(false);
          setTimeout(() => {
            window.location.href = result.paymentLink;
          }, 1500);
          return;
        }

        success(result.message || "Registration submitted successfully!");
        reset();
        setIsVerified(false);

        if (setSignupSuccess) {
          setSignupSuccess(true);
        }
      }
    } catch (err: any) {
      error(err?.message || "Failed to submit registration. Please try again.");
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="virtualSchoolRegistrationCtn">
        {/* Header Back Button & Title */}
        <div className="vsRegHeader">
          <div className="vsRegBackCol" onClick={() => navigate("/virtual-school")}>
            <img src={backArrow} alt="back" className="vsRegBackIcon" />
          </div>
          <div className="vsRegTitleCol">
            <h2 className="vsRegTitle">Katon Virtual School Registration</h2>
            <p className="vsRegSubtitle">
              Complete your registration to enroll in your preferred programme and gain access to live classes, learning resources and assessments.
            </p>
          </div>
        </div>

        {/* Main Registration Form */}
        <form className="vsRegCard" onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          {/* Program Information notice */}
          <div className="vsProgramInfoNotice">
            <div className="vsProgramInfoHeader">
              <h4 className="vsProgramInfoTitle">Below are the details for the classes:</h4>
            </div>
            <ol className="vsProgramInfoList">
              <li>All classes will be hosted Virtually</li>
              <li>The lesson plans will be provided to students & parents / guardians</li>
              <li>Weekly attendance records of students will be sent to parents / guardians every Friday</li>
              <li>Check-in exams will be conducted to assess students' progress, strengths and weaknesses</li>
            </ol>
          </div>

          {/* Account Type Selection */}
          <div className="vsRegSection">
            <label className="vsSectionLabel">How would you like to continue?</label>
            <div className="vsRadioGroup">
              <label className={`vsRadioOption ${hasAccountChoice === "yes" ? "active" : ""}`}>
                <input
                  type="radio"
                  value="yes"
                  {...register("hasAccount", {
                    onChange: (e) => handleRadioChange(e.target.value as "yes" | "no"),
                  })}
                />
                <span className="vsRadioText">I already have a katon account</span>
              </label>

              <label className={`vsRadioOption ${hasAccountChoice === "no" ? "active" : ""}`}>
                <input
                  type="radio"
                  value="no"
                  {...register("hasAccount", {
                    onChange: (e) => handleRadioChange(e.target.value as "yes" | "no"),
                  })}
                />
                <span className="vsRadioText">I am new to Katon</span>
              </label>
            </div>
          </div>

          {/* Verify Account Group (Shown if user has katon account) */}
          {hasAccountChoice === "yes" && (
            <div className="vsRegSection">
              <h4 className="vsSectionTitle">
                Verify Account <span className="vsReqStar">*</span>
              </h4>
              <div className="vsGrid2">
                <div className="vsField">
                  <input
                    type="text"
                    placeholder="Username"
                    autoComplete="off"
                    {...register("username")}
                    className={`vsInput ${errors.username ? "inputError" : ""}`}
                  />
                  {errors.username && <span className="vsErrMsg">{errors.username.message}</span>}
                </div>
                <div className="vsField vsPasswordWrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    autoComplete="new-password"
                    {...register("password")}
                    className={`vsInput ${errors.password ? "inputError" : ""}`}
                  />
                  <span
                    className="vsEyeIcon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <img src={showPassword ? eyeon : eyeoff} alt="toggle password" />
                  </span>
                  {errors.password && <span className="vsErrMsg">{errors.password.message}</span>}
                </div>
              </div>
              <div className={`vsVerifyBtnRow ${isVerified ? "isVerified" : ""}`}>
                <ButtonStyle
                  text={isVerifying ? "Verifying..." : isVerified ? "Verified!" : "Verify Account"}
                  onClick={handleVerifyAccount}
                  disableButton={isVerifying || isVerified || isRedirecting}
                />
              </div>
            </div>
          )}

          {/* Student Information Group */}
          <div className="vsRegSection">
            <h4 className="vsSectionTitle">
              Student Information <span className="vsReqStar">*</span>
            </h4>

            {hasAccountChoice === "yes" && (
              <>
                <div className="vsGrid1">
                  <div className="vsField">
                    <input
                      type="text"
                      placeholder="Full Name"
                      readOnly={true}
                      {...register("fullName")}
                      className={`vsInput ${errors.fullName ? "inputError" : ""} vsReadOnly`}
                    />
                    {errors.fullName && <span className="vsErrMsg">{errors.fullName.message}</span>}
                  </div>
                </div>

                <div className="vsGrid2">
                  <div className="vsField">
                    <input
                      type="email"
                      placeholder="Email"
                      readOnly={true}
                      autoComplete="off"
                      {...register("email")}
                      className={`vsInput ${errors.email ? "inputError" : ""} vsReadOnly`}
                    />
                    {errors.email && <span className="vsErrMsg">{errors.email.message}</span>}
                  </div>
                  <div className="vsField">
                    <input
                      type="text"
                      placeholder="Phone Number"
                      readOnly={true}
                      autoComplete="off"
                      {...register("phone")}
                      className={`vsInput ${errors.phone ? "inputError" : ""} vsReadOnly`}
                    />
                    {errors.phone && <span className="vsErrMsg">{errors.phone.message}</span>}
                  </div>
                </div>

                <div className="vsGrid2">
                  <div className="vsField">
                    <Controller
                      control={control}
                      name="dob"
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            maxDate={dayjs()}
                            value={field.value ? dayjs(field.value) : null}
                            onChange={(newValue) => {
                              field.onChange(newValue ? dayjs(newValue).format("YYYY-MM-DD") : "");
                            }}
                            format="YYYY-MM-DD"
                            slotProps={{
                              textField: {
                                onKeyDown: (e) => e.preventDefault(),
                                placeholder: "DOB",
                                error: !!errors.dob,
                                fullWidth: true,
                                sx: {
                                  "& .MuiInputBase-root": {
                                    height: "41px",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "10px",
                                    fontSize: "12px",
                                    fontFamily: "inherit",
                                    border: errors.dob ? "1px solid #ef4444" : "1px solid #ffffff",
                                    "& fieldset": { border: "none" },
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "12px",
                                    fontSize: "12px",
                                    color: "#111827",
                                    "&::placeholder": {
                                      color: "#B1B1B1",
                                      opacity: 1,
                                    },
                                  },
                                  "& .MuiSvgIcon-root": {
                                    color: "#B1B1B1",
                                    fontSize: "20px",
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                    {errors.dob && <span className="vsErrMsg">{errors.dob.message}</span>}
                  </div>

                  <div className="vsField">
                    <Controller
                      control={control}
                      name="gender"
                      render={({ field }) => (
                        <Select
                          id="gender-existing"
                          placeholder="Gender"
                          options={genderOptions.map((opt) => ({ value: opt, label: opt }))}
                          value={field.value ? { value: field.value, label: field.value } : null}
                          onChange={(selected: any) => field.onChange(selected?.value || "")}
                          styles={getCustomSelectStyles(!!errors.gender)}
                          components={{ DropdownIndicator: CustomDropdownIndicator }}
                          isSearchable={false}
                        />
                      )}
                    />
                    {errors.gender && <span className="vsErrMsg">{errors.gender.message}</span>}
                  </div>
                </div>
              </>
            )}

            {hasAccountChoice === "no" && (
              <>
                <div className="vsGrid1">
                  <div className="vsField">
                    <input
                      type="text"
                      placeholder="Full Name"
                      {...register("fullName")}
                      className={`vsInput ${errors.fullName ? "inputError" : ""}`}
                    />
                    {errors.fullName && <span className="vsErrMsg">{errors.fullName.message}</span>}
                  </div>
                </div>

                <div className="vsGrid2">
                  <div className="vsField">
                    <input
                      type="text"
                      placeholder="Username"
                      autoComplete="off"
                      {...register("username")}
                      className={`vsInput ${errors.username ? "inputError" : ""}`}
                    />
                    {errors.username && <span className="vsErrMsg">{errors.username.message}</span>}
                  </div>
                  <div className="vsField vsPasswordWrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      autoComplete="new-password"
                      {...register("password")}
                      className={`vsInput ${errors.password ? "inputError" : ""}`}
                    />
                    <span
                      className="vsEyeIcon"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <img src={showPassword ? eyeon : eyeoff} alt="toggle password" />
                    </span>
                    {errors.password && <span className="vsErrMsg">{errors.password.message}</span>}
                  </div>
                </div>
                
                <div className="vsGrid2">
                  <div className="vsField">
                    <input
                      type="email"
                      placeholder="Email"
                      autoComplete="off"
                      {...register("email")}
                      className={`vsInput ${errors.email ? "inputError" : ""}`}
                    />
                    {errors.email && <span className="vsErrMsg">{errors.email.message}</span>}
                  </div>
                  <div className="vsField">
                    <input
                      type="text"
                      placeholder="Phone Number"
                      autoComplete="off"
                      {...register("phone")}
                      className={`vsInput ${errors.phone ? "inputError" : ""}`}
                    />
                    {errors.phone && <span className="vsErrMsg">{errors.phone.message}</span>}
                  </div>
                </div>

                <div className="vsGrid2">
                  <div className="vsField">
                    <Controller
                      control={control}
                      name="dob"
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            maxDate={dayjs()}
                            value={field.value ? dayjs(field.value) : null}
                            onChange={(newValue) => {
                              field.onChange(newValue ? dayjs(newValue).format("YYYY-MM-DD") : "");
                            }}
                            format="YYYY-MM-DD"
                            slotProps={{
                              textField: {
                                onKeyDown: (e) => e.preventDefault(),
                                placeholder: "DOB",
                                error: !!errors.dob,
                                fullWidth: true,
                                sx: {
                                  "& .MuiInputBase-root": {
                                    height: "41px",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "10px",
                                    fontSize: "12px",
                                    fontFamily: "inherit",
                                    border: errors.dob ? "1px solid #ef4444" : "1px solid #ffffff",
                                    "& fieldset": { border: "none" },
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "12px",
                                    fontSize: "12px",
                                    color: "#111827",
                                    "&::placeholder": {
                                      color: "#B1B1B1",
                                      opacity: 1,
                                    },
                                  },
                                  "& .MuiSvgIcon-root": {
                                    color: "#B1B1B1",
                                    fontSize: "20px",
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                    {errors.dob && <span className="vsErrMsg">{errors.dob.message}</span>}
                  </div>

                  <div className="vsField">
                    <Controller
                      control={control}
                      name="gender"
                      render={({ field }) => (
                        <Select
                          id="gender-new"
                          placeholder="Gender"
                          options={genderOptions.map((opt) => ({ value: opt, label: opt }))}
                          value={field.value ? { value: field.value, label: field.value } : null}
                          onChange={(selected: any) => field.onChange(selected?.value || "")}
                          styles={getCustomSelectStyles(!!errors.gender)}
                          components={{ DropdownIndicator: CustomDropdownIndicator }}
                          isSearchable={false}
                        />
                      )}
                    />
                    {errors.gender && <span className="vsErrMsg">{errors.gender.message}</span>}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Parent / Guardian Details */}
          <div className="vsRegSection">
            <h4 className="vsSectionTitle">
              Parent / Guardian Details <span className="vsReqStar">*</span>
            </h4>
            <div className="vsGrid2">
              <div className="vsField">
                <input
                  type="text"
                  placeholder="Parent Name"
                  {...register("parentName")}
                  className={`vsInput ${errors.parentName ? "inputError" : ""}`}
                />
                {errors.parentName && <span className="vsErrMsg">{errors.parentName.message}</span>}
              </div>
              <div className="vsField">
                <Controller
                  control={control}
                  name="relationship"
                  render={({ field }) => (
                    <Select
                      id="relationship"
                      placeholder="Relationship"
                      options={relationshipOptions.map((opt) => ({ value: opt, label: opt }))}
                      value={field.value ? { value: field.value, label: field.value } : null}
                      onChange={(selected: any) => field.onChange(selected?.value || "")}
                      styles={getCustomSelectStyles(!!errors.relationship)}
                      components={{ DropdownIndicator: CustomDropdownIndicator }}
                      isSearchable={false}
                    />
                  )}
                />
                {errors.relationship && <span className="vsErrMsg">{errors.relationship.message}</span>}
              </div>
            </div>

            <div className="vsGrid2">
              <div className="vsField">
                <input
                  type="text"
                  placeholder="Parent Phone Number"
                  autoComplete="off"
                  {...register("parentPhone")}
                  className={`vsInput ${errors.parentPhone ? "inputError" : ""}`}
                />
                {errors.parentPhone && <span className="vsErrMsg">{errors.parentPhone.message}</span>}
              </div>
              <div className="vsField">
                <input
                  type="email"
                  placeholder="Parent Email"
                  autoComplete="off"
                  {...register("parentEmail")}
                  className={`vsInput ${errors.parentEmail ? "inputError" : ""}`}
                />
                {errors.parentEmail && <span className="vsErrMsg">{errors.parentEmail.message}</span>}
              </div>
            </div>
          </div>

          {/* Academic Information */}
          <div className="vsRegSection">
            <h4 className="vsSectionTitle">
              Academic Information <span className="vsReqStar">*</span>
            </h4>
            <div className="vsGrid2">
              <div className="vsField">
                <input
                  type="text"
                  placeholder="Current/Previous School Name"
                  {...register("currentPreviousSchoolName")}
                  className={`vsInput ${errors.currentPreviousSchoolName ? "inputError" : ""}`}
                />
                {errors.currentPreviousSchoolName && <span className="vsErrMsg">{errors.currentPreviousSchoolName.message}</span>}
              </div>
              <div className="vsField">
                <Controller
                  control={control}
                  name="academicLevel"
                  render={({ field }) => (
                    <Select
                      id="academicLevel"
                      placeholder="Academic Level"
                      options={academicLevelOptions.map((opt) => ({ value: opt, label: opt }))}
                      value={field.value ? { value: field.value, label: field.value } : null}
                      onChange={(selected: any) => field.onChange(selected?.value || "")}
                      styles={getCustomSelectStyles(!!errors.academicLevel)}
                      components={{ DropdownIndicator: CustomDropdownIndicator }}
                      isSearchable={false}
                    />
                  )}
                />
                {errors.academicLevel && <span className="vsErrMsg">{errors.academicLevel.message}</span>}
              </div>
            </div>

            <div className="vsGrid2">
              <div className="vsField">
                <input
                  type="text"
                  placeholder="BECE Number"
                  maxLength={12}
                  {...register("beceNumber")}
                  onInput={(e: React.FormEvent<HTMLInputElement>) => {
                    e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, "");
                  }}
                  className={`vsInput ${errors.beceNumber ? "inputError" : ""}`}
                />
                {errors.beceNumber && <span className="vsErrMsg">{errors.beceNumber.message}</span>}
              </div>
            </div>
          </div>

          {/* Choose a Program */}
          <div className="vsRegSection">
            <h4 className="vsSectionTitle">
              Choose a Program <span className="vsReqStar">*</span>
            </h4>
            <div className="vsGrid2">
              <div className="vsField">
                <Controller
                  control={control}
                  name="program"
                  render={({ field }) => {
                    const progOptions = programmeList.map((prog: IProgrammeItem) => {
                      const curr = prog.currency || "GHS";
                      const priceVal = prog.price || "";
                      const val = prog.programmeId || prog.name;
                      const lbl = priceVal ? `${prog.name} (${curr} ${priceVal})` : prog.name;
                      return { value: val, label: lbl };
                    });
                    const selectedOption = progOptions.find((o) => o.value === field.value) || null;
                    return (
                      <Select
                        id="program"
                        placeholder="Program"
                        options={progOptions}
                        value={selectedOption}
                        onChange={(selected: any) => field.onChange(selected?.value || "")}
                        styles={getCustomSelectStyles(!!errors.program)}
                        components={{ DropdownIndicator: CustomDropdownIndicator }}
                        isSearchable={false}
                      />
                    );
                  }}
                />
                {errors.program && <span className="vsErrMsg">{errors.program.message}</span>}
              </div>
            </div>
          </div>

          {/* Communication Preferences */}
          <div className="vsRegSection">
            <h4 className="vsSectionTitle">
              Communication Preferences <span className="vsReqStar">*</span>
            </h4>
            <div className="vsGrid2">
              <div className="vsField">
                <input
                  type="email"
                  placeholder="Email"
                  autoComplete="off"
                  {...register("preferredEmail")}
                  className={`vsInput ${errors.preferredEmail ? "inputError" : ""}`}
                />
                {errors.preferredEmail && <span className="vsErrMsg">{errors.preferredEmail.message}</span>}
              </div>
              <div className="vsField">
                <input
                  type="text"
                  placeholder="Contact Number"
                  autoComplete="off"
                  {...register("preferredPhone")}
                  className={`vsInput ${errors.preferredPhone ? "inputError" : ""}`}
                />
                {errors.preferredPhone && <span className="vsErrMsg">{errors.preferredPhone.message}</span>}
              </div>
            </div>

            <div className="vsInfoNotice">
              <InfoOutlined fontSize="small" className="vsInfoIcon" />
              <span>
                Provide your preferred email address and contact number to receive important updates related to your virtual learning journey.
              </span>
            </div>

            <div className="vsCommunicationGroups">
              <label className="vsSubLabel">WhatsApp Group Consent</label>
              <div className="vsCheckboxList">
                {communicationGroupOptions.map((opt) => (
                  <label key={opt} className="vsCheckboxOption">
                    <input
                      type="checkbox"
                      value={opt}
                      checked={watch("communicationGroups")?.includes(opt) || false}
                      onChange={(e) => {
                        const current = getValues("communicationGroups") || [];
                        if (e.target.checked) {
                          setValue("communicationGroups", [...current, opt], { shouldValidate: true });
                        } else {
                          setValue("communicationGroups", current.filter((x) => x !== opt), { shouldValidate: true });
                        }
                      }}
                    />
                    <span className="vsCheckboxText">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="vsRegSection">
            <h4 className="vsSectionTitle">
              Additional Information <span className="vsReqStar">*</span>
            </h4>
            <div className="vsGrid2">
              <div className="vsField">
                <Controller
                  control={control}
                  name="location"
                  render={({ field }) => (
                    <Select
                      id="location"
                      placeholder="Select Your Location"
                      options={locationOptions.map((opt) => ({ value: opt, label: opt }))}
                      value={field.value ? { value: field.value, label: field.value } : null}
                      onChange={(selected: any) => field.onChange(selected?.value || "")}
                      styles={getCustomSelectStyles(!!errors.location)}
                      components={{ DropdownIndicator: CustomDropdownIndicator }}
                      isSearchable={false}
                    />
                  )}
                />
                {errors.location && <span className="vsErrMsg">{errors.location.message}</span>}
              </div>

              <div className="vsField">
                <Controller
                  control={control}
                  name="referralSource"
                  render={({ field }) => (
                    <Select
                      id="referral"
                      placeholder="Where you got to know about us"
                      options={referralSourceOptions.map((opt) => ({ value: opt, label: opt }))}
                      value={field.value ? { value: field.value, label: field.value } : null}
                      onChange={(selected: any) => field.onChange(selected?.value || "")}
                      styles={getCustomSelectStyles(!!errors.referralSource)}
                      components={{ DropdownIndicator: CustomDropdownIndicator }}
                      isSearchable={false}
                      isClearable
                    />
                  )}
                />
                {errors.referralSource && <span className="vsErrMsg">{errors.referralSource.message}</span>}
              </div>
            </div>
          </div>

          {/* Data Information */}
          <div className="vsRegSection">
            <h4 className="vsSectionTitle">
              Data Information
            </h4>
            <div className="vsGrid2">
              <div className="vsField">
                <input
                  type="text"
                  placeholder="Registered SIM Name"
                  {...register("registeredSimName")}
                  className={`vsInput ${errors.registeredSimName ? "inputError" : ""}`}
                />
                {errors.registeredSimName && <span className="vsErrMsg">{errors.registeredSimName.message}</span>}
              </div>
              <div className="vsField">
                <Controller
                  control={control}
                  name="networkProvider"
                  render={({ field }) => (
                    <Select
                      id="networkProvider"
                      placeholder="Network Provider"
                      options={networkProviderOptions.map((opt) => ({ value: opt, label: opt }))}
                      value={field.value ? { value: field.value, label: field.value } : null}
                      onChange={(selected: any) => field.onChange(selected?.value || "")}
                      styles={getCustomSelectStyles(!!errors.networkProvider)}
                      components={{ DropdownIndicator: CustomDropdownIndicator }}
                      isSearchable={false}
                      isClearable
                    />
                  )}
                />
                {errors.networkProvider && <span className="vsErrMsg">{errors.networkProvider.message}</span>}
              </div>
            </div>

            <div className="vsFreeDataConsent">
              <label className="vsSubLabel">Select Number for Free Data</label>
              <div className="vsCheckboxList">
                {dataOptInTargetsOptions.map((opt) => (
                  <label key={opt} className="vsCheckboxOption">
                    <input
                      type="checkbox"
                      checked={watch("dataOptInFor") === opt}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setValue("dataOptInFor", opt, { shouldValidate: true });
                        } else {
                          setValue("dataOptInFor", "", { shouldValidate: true });
                        }
                      }}
                    />
                    <span className="vsCheckboxText">{opt}</span>
                  </label>
                ))}
              </div>
              <div className="vsInfoNotice vsFreeDataNotice">
                <InfoOutlined fontSize="small" className="vsInfoIcon" />
                <span>
                  For free data, payment must made before the program start date.
                </span>
              </div>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="vsRegFooter">
            <ButtonStyle
              text={isRegistering || isRedirecting ? "Please wait..." : "Register and Pay"}
              type="submit"
              disableButton={isVerifying || isRegistering || isRedirecting}
            />
          </div>
        </form>
      </div>
    </FormProvider>
  );
};

export default VirtualSchoolSignup;
